import React, { useState, useRef } from "react";
import { useFormContext } from "./FormContext";
import {
  KeyboardArrowRight,
  KeyboardArrowDown,
  AdjustOutlined,
  RadioButtonChecked,
} from "@mui/icons-material";
import styled from "styled-components";

const buildTree = (data, filterObject) => {
  const { level1, level2, level3, level4, level5, mapping , context } = data;
 
  // Get the count of levels
  const levelCount = Object.keys(mapping).length;
  
  // consider last level as leaf node and for tree view consider only n-1 levels.
  const isProductHierarchy = context === 'productheirarchy';

  const levelSize = levelCount - (isProductHierarchy ? 0 : 1);
  
  const level1Items = isProductHierarchy
    ? level1.filter(el => el.level1_id === filterObject.level1_id)
    : level1;
  
  // Initialize level maps based on levelSize
  const levelMaps = {
    level1: level1Items
    .map((item) => ({
      ...item,
      children: [],
      level: "level1",
      id: item.level1_id,
    })).sort((a, b) => a.name.localeCompare(b.name)),
  };

  if (levelSize >= 2) {
    levelMaps['level2'] = level2.map((item) => ({
      ...item,
      children: [],
      level: "level2",
      id: item.level2_id,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  if (levelSize >= 3) {
    levelMaps.level3 = level3.map((item) => ({
      ...item,
      children: [],
      level: "level3",
      id: item.level3_id,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  if (levelSize >= 4) {
    levelMaps.level4 = level4.map((item) => ({
      ...item,
      children: [],
      level: "level4",
      id: item.level4_id,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  if (levelSize >= 5) {
    levelMaps.level5 = level5.map((item) => ({
      ...item,
      children: [],
      level: "level5",
      id: item.level5_id,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  // Process mappings based on levelSize
  if (levelSize >= 5) {
    levelMaps.level5.forEach((item) => {
      const parent = levelMaps.level4.find(
        (lvl4) =>
          lvl4.level1_id == item.level1_id &&
          lvl4.level2_id == item.level2_id &&
          lvl4.level3_id == item.level3_id &&
          lvl4.level4_id == item.level4_id
      );
      if (parent) {
        parent.children.push(item);
      }
    });
  }

  if (levelSize >= 4) {
    levelMaps.level4.forEach((item) => {
      const parent = levelMaps.level3.find(
        (lvl3) =>
          lvl3.level1_id == item.level1_id &&
          lvl3.level2_id == item.level2_id &&
          lvl3.level3_id == item.level3_id
      );
      if (parent) {
        parent.children.push(item);
      }
    });
  }

  if (levelSize >= 3) {
    levelMaps.level3.forEach((item) => {
      const parent = levelMaps.level2.find(
        (lvl2) =>
          lvl2.level2_id == item.level2_id && 
          lvl2.level1_id == item.level1_id
      );
      if (parent) {
        parent.children.push(item);
      }
    });
  }

  if (levelSize >= 2) {
    levelMaps.level2.forEach((item) => {
      const parent = levelMaps.level1.find(
        (lvl1) => lvl1.level1_id == item.level1_id
      );
      if (parent) {
        parent.children.push(item);
      }
    });
  }

  return levelMaps.level1;
};

const TreeNode = ({
  node,
  parentIds = [],
  onClick,
  selectedNodeId,
  setSelectedTreeNode,
  treeviewBoxRef
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleClick = () => {
    // console.log({nodeId:node.id,level:node.level,parentIds});
    const nodeId = [...parentIds, node.id].join("_");
    if (onClick) onClick(nodeId); // Notify parent about the selected node

    let treeNodeObject = {
      level1_id: null,
      level2_id: null,
      level3_id: null,
      level4_id: null,
      level5_id: null,
      level: node.level,
    };
    if (node.level === "level2") {
      treeNodeObject.level1_id = parentIds[0];
      treeNodeObject.level2_id = node.id;
    } else if (node.level === "level1") {
      treeNodeObject.level1_id = node.id;
      treeNodeObject.level2_id = null;
    } else if (node.level === "level3") {
      treeNodeObject.level1_id = parentIds[0];
      treeNodeObject.level2_id = parentIds[1];
      treeNodeObject.level3_id = node.id;
    } else if (node.level === "level4") {
      treeNodeObject.level1_id = parentIds[0];
      treeNodeObject.level2_id = parentIds[1];
      treeNodeObject.level3_id = parentIds[2];
      treeNodeObject.level4_id = node.id;
    
    } else if (node.level === "level5") {
      treeNodeObject.level1_id = parentIds[0];
      treeNodeObject.level2_id = parentIds[1];
      treeNodeObject.level3_id = parentIds[2];
      treeNodeObject.level4_id = parentIds[3];
      treeNodeObject.level5_id = node.id;
    }
    //console.log(treeNodeObject)
    setSelectedTreeNode(treeNodeObject);


  };

  const nodeId = [...parentIds, node.id].join("_"); // Generate unique ID
  const isSelected = nodeId === selectedNodeId;
  return (
    <div style={{ marginLeft: "20px" }}>
      <div
        id={nodeId}
        onClick={(e) => {
          e.stopPropagation(); // Prevent parent clicks
          // if (treeviewBoxRef.current) {
          //   treeviewBoxRef.current.scrollIntoView({
          //     behavior: 'smooth', // Optional: Makes scrolling smooth
          //     block: 'start',     // Optional: Aligns to the top of the viewport
          //   });
          // }
          setExpanded(!expanded);
          handleClick();
        }}
        style={{
          cursor: "pointer",
          fontWeight:
            node.children?.length || node.level === "level1"
              ? "bold"
              : "normal",
          // color: isSelected ? "#0C3C54" : "black", // Apply bold style if selected
        }}
      >
        {node.children?.length ? (
          expanded ? (
            <KeyboardArrowDown fontSize="large" />
          ) : (
            <KeyboardArrowRight fontSize="large" />
          )
        ) : (
          <AdjustOutlined
            fontSize="small"
            style={{ marginRight: "8px", marginLeftLeft: "3px" }}
          />
        )}
        {"  "}
        {node.name}{" "}
        {isSelected ? <RadioButtonChecked fontSize="small" /> : <></>}
      </div>
      {expanded && node.children?.length > 0 && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={`${child.level}_id_${child.id}`} // Unique key with level prefix
              node={child}
              parentIds={[...parentIds, node.id]} // Pass parent's ID in the hierarchy
              setSelectedTreeNode={setSelectedTreeNode}
              onClick={onClick} // Pass onClick down
              selectedNodeId={selectedNodeId} // Pass the selected node ID
              treeviewBoxRef={treeviewBoxRef}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Stage3Heirarchy = ({ heirarchyData: data, setSelectedTreeNode }) => {
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const treeviewBoxRef = useRef(null);
  const  { formData } = useFormContext();
  const  selProductCategory = formData?.stage2?.product_category;

    const treeData = buildTree(data , { level1_id : selProductCategory });
  return (
    <Wrapper id="treeview-box"  ref={treeviewBoxRef} >
      {treeData.map((rootNode) => (
        <TreeNode
          key={rootNode.level1_id}
          node={rootNode}
          setSelectedTreeNode={setSelectedTreeNode}
          onClick={setSelectedNodeId} // Set the selected node ID
          selectedNodeId={selectedNodeId} // Pass the selected node ID
          treeviewBoxRef={treeviewBoxRef}
        />
      ))}
    </Wrapper>
  );
};

export default Stage3Heirarchy;

const Wrapper = styled.div`
  margin-top: 10px;
`;
